import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Eye, EyeOff, ChevronDown, Loader2, Check, Fingerprint,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import LandingLegalFooter, { LegalModal } from "@/components/gateway/LandingLegalFooter";

const RUST = "#C05621";
const HARD = "shadow-[4px_4px_0px_#1A1A1A]";
const PRESS =
  "transition-[transform,box-shadow] duration-150 active:translate-x-[3px] active:translate-y-[3px] active:shadow-none";

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#C05621] mb-1.5">{label}</span>
      <input
        data-testid={testid}
        className={`w-full bg-white border-2 border-[#1A1A1A] ${HARD} rounded-xl px-3.5 py-3 font-mono text-sm text-[#1A1A1A] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#FFD028]`}
        {...props}
      />
    </label>
  );
}

function YellowButton({ children, testid, onClick, disabled, busy }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled || busy}
      className={`w-full flex items-center justify-center gap-2 bg-[#FFD028] text-[#1A1A1A] border-2 border-[#1A1A1A] ${HARD} ${PRESS} rounded-xl py-3.5 font-black text-sm uppercase tracking-[0.12em] disabled:opacity-60`}
    >
      {busy ? <Loader2 size={18} className="animate-spin" /> : children}
    </button>
  );
}

/* --------------------------------- SIGN IN -------------------------------- */
function SignInPane({ go }) {
  const { signIn, signInWithPasskey } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      await signIn(email, password);
      toast.success("Welcome back — routing to Central Core");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
      setBusy(false);
    }
  };

  const passkeyLogin = async () => {
    setPkBusy(true);
    try {
      await signInWithPasskey(email || undefined);
      toast.success("Unlocked with passkey ✓");
    } catch (err) {
      const { passkeyErrorMessage } = await import("@/lib/passkey");
      toast.error(passkeyErrorMessage(err));
      setPkBusy(false);
    }
  };

  return (
    <div data-testid="auth-signin-pane">
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Username / Email" testid="signin-email" type="text" inputMode="email"
          autoCapitalize="none" autoCorrect="off" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="username or you@studio.com"
        />
        <div>
          <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#C05621] mb-1.5">Password</span>
          <div className="relative">
            <input
              data-testid="signin-password" type={show ? "text" : "password"} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className={`w-full bg-white border-2 border-[#1A1A1A] ${HARD} rounded-xl px-3.5 py-3 pr-11 font-mono text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#FFD028]`}
            />
            <button type="button" data-testid="signin-toggle-password" onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <YellowButton testid="signin-submit" busy={busy}>
          Sign In <ArrowRight size={18} strokeWidth={2.6} />
        </YellowButton>
      </form>

      <div className="mt-3 flex items-center gap-3">
        <span className="flex-1 h-px bg-neutral-700" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">or</span>
        <span className="flex-1 h-px bg-neutral-700" />
      </div>
      <button type="button" data-testid="signin-passkey-btn" onClick={passkeyLogin} disabled={pkBusy}
        className={`mt-3 w-full flex items-center justify-center gap-2 bg-[#111] border-2 border-[#FFD028] rounded-xl px-3.5 py-3 font-mono text-[11px] font-black uppercase tracking-widest text-[#FFD028] ${PRESS} ${HARD} disabled:opacity-50`}>
        {pkBusy ? <Loader2 size={16} className="animate-spin" /> : <Fingerprint size={16} />}
        Sign in with Fingerprint / Passkey
      </button>

      <div className="flex items-center justify-end mt-4">
        <button data-testid="forgot-password-link" onClick={() => go("FORGOT")}
          className="font-mono text-[10px] uppercase tracking-widest font-bold" style={{ color: RUST }}>
          Forgot Password?
        </button>
      </div>
    </div>
  );
}

/* --------------------------------- SIGN UP -------------------------------- */
function SignUpPane({ go, onPending }) {
  const { signUp } = useSession();
  const [form, setForm] = useState({ name: "", email: "", password: "", age_text: "", birth_date: "" });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [legal, setLegal] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const { name, email, password, age_text, birth_date } = form;
    if (!name || !email || !password || !age_text || !birth_date) {
      toast.error("All fields are required");
      return;
    }
    if (!agreed) {
      toast.error("Please accept the Terms of Service & Privacy Policy");
      return;
    }
    setBusy(true);
    try {
      const data = await signUp({ ...form, accepted_terms: true });
      onPending(data.email, data.dev_code);
      go("CODE");
      toast.success("Verification code dispatched");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-signup-pane">
      <form onSubmit={submit} className="space-y-3.5">
        <Field label="Full Name" testid="signup-name" value={form.name} onChange={set("name")} placeholder="David McNaughton" />
        <Field label="Email" testid="signup-email" type="email" value={form.email} onChange={set("email")} placeholder="you@studio.com" />
        <Field label="Password" testid="signup-password" type="password" value={form.password} onChange={set("password")} placeholder="min 6 chars" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Age" testid="signup-age-text" inputMode="numeric" value={form.age_text} onChange={set("age_text")} placeholder="54" />
          <Field label="Date of Birth" testid="signup-birth-date" type="date" value={form.birth_date} onChange={set("birth_date")} />
        </div>

        <button type="button" data-testid="signup-terms-checkbox" onClick={() => setAgreed((v) => !v)}
          className="w-full flex items-start gap-2.5 text-left pt-1">
          <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 grid place-items-center transition-colors ${agreed ? "bg-[#FFD028] border-[#FFD028]" : "border-neutral-600 bg-transparent"}`}>
            {agreed && <Check size={13} strokeWidth={4} className="text-[#1A1A1A]" />}
          </span>
          <span className="font-mono text-[10px] leading-relaxed text-neutral-400">
            I have read and agree to the{" "}
            <span onClick={(e) => { e.stopPropagation(); setLegal("terms"); }} data-testid="signup-terms-link" className="text-[#FFD028] font-bold underline underline-offset-2">Terms of Service</span>{" "}and{" "}
            <span onClick={(e) => { e.stopPropagation(); setLegal("privacy"); }} data-testid="signup-privacy-link" className="text-[#FFD028] font-bold underline underline-offset-2">Privacy Policy</span>.
          </span>
        </button>

        <div className="pt-1">
          <YellowButton testid="signup-submit" busy={busy} disabled={!agreed}>
            Continue <ArrowRight size={18} strokeWidth={2.6} />
          </YellowButton>
        </div>
      </form>
      {legal && <LegalModal docKey={legal} onClose={() => setLegal(null)} />}
    </div>
  );
}

/* ----------------------------- COMBINED CARD ------------------------------ */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default function AuthCard({ go, onPending, defaultTab = "signin" }) {
  const { startGoogleLogin } = useSession();
  const [tab, setTab] = useState(defaultTab);
  const isSignin = tab === "signin";

  return (
    <div className="flex-1 flex flex-col p-4 overflow-y-auto no-scrollbar" data-testid="auth-card">
      <div className="flex items-center justify-between mb-5">
        <button data-testid="auth-cancel-btn" onClick={() => go("LANDING")}
          className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-neutral-400 hover:text-[#FFD028]">
          <ArrowLeft size={14} /> Back
        </button>
        <button data-testid="lang-select" onClick={() => toast("More languages coming soon", { icon: "🌐" })}
          className="flex items-center gap-1 bg-[#111] border-2 border-neutral-700 rounded-lg px-2 py-1 font-mono text-[10px] text-white">
          🇺🇸 English <ChevronDown size={12} />
        </button>
      </div>

      <div className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFD028]">Identity Portal</p>
        <h1 className="font-serif text-4xl text-white leading-none mt-1">{isSignin ? "Welcome Back" : "Create Account"}</h1>
      </div>

      {/* Tab toggle */}
      <div className="flex border-2 border-[#FFD028] rounded-xl overflow-hidden mb-5" data-testid="auth-tab-toggle">
        {[
          { k: "signin", label: "Sign In" },
          { k: "signup", label: "Sign Up" },
        ].map((t) => (
          <button key={t.k} data-testid={`auth-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`flex-1 py-2.5 font-black text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === t.k ? "bg-[#FFD028] text-[#1A1A1A]" : "bg-transparent text-neutral-400"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {isSignin ? <SignInPane go={go} /> : <SignUpPane go={go} onPending={onPending} />}

      {/* Continue with Google — shared by both tabs */}
      <div className="mt-4 flex items-center gap-3">
        <span className="flex-1 h-px bg-neutral-700" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">or continue with</span>
        <span className="flex-1 h-px bg-neutral-700" />
      </div>
      <button type="button" data-testid="google-login-btn" onClick={() => startGoogleLogin()}
        className={`mt-3 w-full flex items-center justify-center gap-2.5 bg-white border-2 border-[#1A1A1A] rounded-xl px-3.5 py-3 font-black text-[13px] tracking-tight text-[#1A1A1A] shadow-[4px_4px_0px_#1A1A1A] ${PRESS}`}>
        <GoogleGlyph /> Continue with Google
      </button>

      <div className="mt-auto pt-6">
        <LandingLegalFooter />
      </div>
    </div>
  );
}
